import {DrawSource} from '../enums/DrawSource'
import {GeometryType} from '../enums/GeometryType'
import {IFeatureLayerInfo, INomenclatureSourceVectorRenderer, ISourceRendererPart} from '../interfaces/models'
import {IFeatureLayerInfos} from '../interfaces/reducers'
import {IMapService} from '../interfaces/services'
import {
  Base,
  Circle,
  Feature,
  FeatureStatus,
  Fill,
  Geometry,
  Icon,
  LineString,
  MultiPoint,
  getLength,
  Stroke,
  Style,
  Text,
  VectorSource
} from '../models'
import {Map} from '../models/ol/Map'

let cache: {[guid: string]: Style[]} = {}
let targetStyleScale: number = 1

export const getResolutions = (): {[zoomLevel: number]: number} => {
  return {
    0: 156543.03392804097,
    1: 78271.51696402048,
    2: 39135.75848201024,
    3: 19567.87924100512,
    4: 9783.93962050256,
    5: 4891.96981025128,
    6: 2445.98490512564,
    7: 1222.99245256282,
    8: 611.49622628141,
    9: 305.748113140705,
    10: 152.8740565703525,
    11: 76.43702828517625,
    12: 38.21851414258813,
    13: 19.109257071294063,
    14: 9.554628535647032,
    15: 4.777314267823516,
    16: 2.388657133911758,
    17: 1.194328566955879,
    18: 0.5971642834779395,
    19: 0.29858214173896974,
    20: 0.14929107086948487,
    21: 0.07464553543474244,
    22: 0.03732276771737122,
    23: 0.01866138385868561,
    24: 0.009330691929342804
  }
}

export const requestImages = (infos: IFeatureLayerInfos): Promise<{[url: string]: HTMLImageElement}> => {
  //used to build vector images and browser cache them
  const imgRenderPartsUrls: {[url: string]: {}} = {}
  for (const key in infos) {
    const i = infos[key]
    if (i.sourceDescription.vectorRenderer) {
      if (i.sourceDescription.vectorRenderer.nomenclature) {
        for (const nomenclatureValueKey in i.sourceDescription.vectorRenderer.nomenclature.values) {
          i.sourceDescription.vectorRenderer.nomenclature.values[nomenclatureValueKey].forEach(renderer => {
            if (renderer.img) {
              imgRenderPartsUrls[renderer.img.src] = {}
            }
          })
        }
      }
      if (i.sourceDescription.vectorRenderer.def) {
        i.sourceDescription.vectorRenderer.def.forEach(renderer => {
          if (renderer.img) {
            imgRenderPartsUrls[renderer.img.src] = {}
          }
        })
      }
    }
  }

  const promises: Array<Promise<{url: string; node: HTMLImageElement}>> = []
  for (const url in imgRenderPartsUrls) {
    promises.push(
      new Promise((resolve, reject) => {
        getVectorImage(url, 100).then(node => {
          resolve({url, node})
        })
      })
    )
  }
  return new Promise((r, j) => {
    const result: {[url: string]: HTMLImageElement} = {}
    Promise.all(promises).then(is => {
      is.forEach(i => {
        result[i.url] = i.node
      })
      r(result)
    })
  })
}

const getVectorImage = (src: string, size: number): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.setAttribute('width', size.toString())
    canvas.setAttribute('height', size.toString())
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.style.width = size.toString()
    img.style.height = size.toString()
    img.onload = function () {
      ctx!.drawImage(img, 0, 0, size, size)
      const i = document.createElement('img')
      i.src = canvas.toDataURL()
      resolve(i)
    }
    img.src = src
  })
}

const nomenclatureRenderFunction = (
  mapService: IMapService,
  layerInfo: IFeatureLayerInfo,
  nomenclatureRenderer?: INomenclatureSourceVectorRenderer
): ((feature: Feature, resulution: number) => Style | Style[]) | null => {
  return (feature: Feature, resulution: number) => {
    const fProps = feature.getProperties()
    if (nomenclatureRenderer) {
      const nomValue = fProps[nomenclatureRenderer!.field].toString()
      for (const value in nomenclatureRenderer!.values) {
        if (nomValue == value.toString()) {
          return rendererPartToStyle(nomenclatureRenderer!.values[value], feature)
        }
      }
    }
    return new Style()
  }
}

export const getVectorLayerStyle = (
  layerInfo: IFeatureLayerInfo,
  mapService: IMapService
): ((feature: Feature, resulution: number) => Style | Style[]) | null => {
  if (layerInfo.sourceDescription.vectorRenderer) {
    if (layerInfo.sourceDescription.vectorRenderer.nomenclature) {
      return nomenclatureRenderFunction(mapService, layerInfo, layerInfo.sourceDescription.vectorRenderer.nomenclature)
    }
  }
  return defaultRendererFunction(mapService, layerInfo)
}

const getIconAnchorUnits = (units?: string): 'fraction' | 'pixels' => {
  if (units) {
    if (units == 'fraction') return 'fraction'
    if (units == 'pixels') return 'pixels'
  }
  return 'fraction'
}

export const mapToBlob = (vectorRenderingMap: Map): Promise<Blob> => {
  Array.prototype.forEach.call(
    document.querySelectorAll('style,[rel="stylesheet"],[type="text/css"]'),
    function (element: any) {
      try {
        if (element.href.indexOf('fonts.googleapis.com') != -1) {
          element.parentNode.removeChild(element)
        }
      } catch (err) {}
    }
  )
  let solved = false
  return new Promise((resolve, reject) => {
    const removes: Base[] = []
    vectorRenderingMap.getLayers().forEach((l: Base) => {
      if (l.moreInfo && l.moreInfo.type == 'regular') {
        if (l.getSource() instanceof VectorSource) {
          const extent = vectorRenderingMap.getView().calculateExtent()
          const feats = (l.getSource() as VectorSource).getFeaturesInExtent(extent)
          if (feats.length == 0) {
            removes.push(l)
          } else if (
            vectorRenderingMap.getView().getResolution()! > l.getMaxResolution() ||
            vectorRenderingMap.getView().getResolution()! < l.getMinResolution()
          ) {
            removes.push(l)
          }
        }
      }
    })
    removes.forEach(l => {
      vectorRenderingMap.removeLayer(l)
    })
    vectorRenderingMap.once('rendercomplete', (event: any) => {
      if (!solved) {
        solved = true
        const canvas = vectorRenderingMap.getViewport().querySelector('canvas') as HTMLCanvasElement
        setTimeout(() => {
          if ((navigator as any).msSaveBlob as any) {
            resolve((canvas as any).msToBlob())
            removes.forEach(l => {
              vectorRenderingMap.addLayer(l)
            })
          } else {
            canvas!.toBlob((blob: Blob) => {
              resolve(blob)
              removes.forEach(l => {
                vectorRenderingMap.addLayer(l)
              })
            })
          }
        }, 100)
      }
    })
    vectorRenderingMap.renderSync()
    return
  })
}

export const setStyleScale = (scale: number) => {
  cache = {}
  targetStyleScale = scale
}

const getLineAngle = (line: LineString) => {
  if (line) {
    const coords = line.getCoordinates()

    if (coords.length > 0) {
      const deltaX = coords[1][0] - coords[0][0]
      const deltaY = coords[1][1] - coords[0][1]
      return Math.atan2(deltaY, deltaX)
    }
  }
  return 0
}

const rendererPartToStyle = (sourceRendererParts: ISourceRendererPart[], feature: Feature): Style[] => {
  if (cache[feature.guid] && feature.status == FeatureStatus.Unchanged && !feature.get('___styleDelta')) {
    return cache[feature.guid]
  }
  let putInCache = true
  const styles: Style[] = []
  sourceRendererParts.forEach(sourceRendererPart => {
    const style: Style = new Style()
    style.setZIndex(sourceRendererPart.zIndex || 1)
    if (sourceRendererPart.img) {
      style.setImage(
        new Icon({
          anchor: sourceRendererPart.img.anchor || [0.5, 0.5],
          anchorXUnits: getIconAnchorUnits(sourceRendererPart.img.anchorXUnits),
          anchorYUnits: getIconAnchorUnits(sourceRendererPart.img.anchorYUnits),
          src: sourceRendererPart.img.src,
          scale: sourceRendererPart.img.scale ? sourceRendererPart.img.scale * targetStyleScale : targetStyleScale
        })
      )
    }
    if (sourceRendererPart.stroke) {
      if (feature.get('Radiusable')) {
        style.setStroke(
          new Stroke({
            color: 'rgba(0,0,0,0)',
            width: 1
          })
        )
      } else {
        style.setStroke(
          new Stroke({
            color:
              sourceRendererPart.stroke.color ||
              (sourceRendererPart.stroke.width === 0 ? 'rgba(0,0,0,0)' : 'rgb(0, 153, 255)'),
            width:
              (sourceRendererPart.stroke.width == undefined ? 2 : sourceRendererPart.stroke.width) * targetStyleScale,
            lineDash: sourceRendererPart.stroke.lineDash
          })
        )
      }
    }
    if (sourceRendererPart.circle) {
      style.setImage(
        new Circle({
          radius: (sourceRendererPart.circle.radius || 5) * targetStyleScale,
          fill: new Fill({
            color: sourceRendererPart.circle.fill ? sourceRendererPart.circle.fill.color : 'rgba(255, 255, 255, 1)'
          }),
          stroke: new Stroke({
            color: sourceRendererPart.circle.stroke ? sourceRendererPart.circle.stroke.color : 'rgb(0, 153, 255)',
            width:
              (!sourceRendererPart.circle.stroke || sourceRendererPart.circle.stroke.width == undefined
                ? 2
                : sourceRendererPart.circle.stroke.width) * targetStyleScale
          })
        })
      )
    }

    if (sourceRendererPart.geometry) {
      eval('window.geometryEval = function (feature) { ' + sourceRendererPart.geometry + ';}')
      const geom = (window as any).geometryEval(feature) || ''
      if (feature.get('___styleDelta') && geom && (geom as Geometry).getType() == 'Point') {
        geom.setCoordinates([
          geom.getCoordinates()[0] + feature.get('___styleDelta')[0],
          geom.getCoordinates()[1] + feature.get('___styleDelta')[1]
        ])
      }
      style.setGeometry(geom)
    }
    if (sourceRendererPart.fill) {
      style.setFill(
        new Fill({
          color: sourceRendererPart.fill.color || 'rgba(255, 255, 255, 1)'
        })
      )
    }
    let angleToration = 0
    if (sourceRendererPart.text) {
      if (sourceRendererPart.text.textFunction) {
        putInCache = false
        if (sourceRendererPart.text.textFunction == 'distance') {
          if (feature.get('Measurable')) {
            ;(window as any).textEval = function () {
              if (feature.get('Distance') && !isNaN(parseFloat(feature.get('Distance')))) {
                return parseFloat(feature.get('Distance')).toFixed(2)
              }
              const length = getLength(feature.getGeometry())
              const output = (Math.round(length * 100) / 100).toFixed(2)
              return output
            }
            angleToration = -getLineAngle(feature.getGeometry() as LineString)
            if (angleToration > 1.57 || angleToration < -1.57) {
              angleToration = angleToration - Math.PI
            }
          } else if (feature.get('Radiusable')) {
            ;(window as any).textEval = function () {
              if (feature.get('Radius')) {
                return feature.get('Radius')
              }
            }
            const coords = feature.getGeometry().getCoordinates() as any
            angleToration = -getLineAngle(new LineString([coords[0], coords[2]] as any))
            // if(angleToration > 1.570 || angleToration < - 1.570){
            //     angleToration = angleToration - Math.PI;
            // }
          }
        } else {
          eval('window.textEval = function (feature) {return ' + sourceRendererPart.text.textFunction + ';}')
        }
      }
      let rotation = [sourceRendererPart.text.offsetX || 0, sourceRendererPart.text.offsetY || 0]
      if (sourceRendererPart.text.offsetFunc) {
        putInCache = false
        eval('window.offsetFunc = function(feature) {return ' + sourceRendererPart.text.offsetFunc + '}')
        rotation = (window as any).offsetFunc(feature)
      }
      const text =
        (window as any).textEval && (window as any).textEval(feature)
          ? (window as any).textEval(feature).toString()
          : sourceRendererPart.text.text || ''

      // degree to radians

      const textRotation = sourceRendererPart.text.rotation || 0
      const angleToradian = Math.PI / 180
      style.setText(
        new Text({
          text,
          overflow: !!sourceRendererPart.text.overflow,
          fill: new Fill({
            color: sourceRendererPart.text.fillColor || 'rgba(255, 255, 255, 1)'
          }),

          rotation: textRotation == 0 ? 0 : textRotation * angleToradian,
          offsetX: rotation[0] * targetStyleScale,
          offsetY: rotation[1] * targetStyleScale,
          font: sourceRendererPart.text.font || '16px sans-serif',
          scale: targetStyleScale
        })
      )
    }
    styles.push(style)
  })
  if (putInCache) {
    cache[feature.guid] = styles
  }

  return styles
}
const defaultRendererFunction = (
  mapService: IMapService,
  layerInfo: IFeatureLayerInfo
): ((feature: Feature, resulution: number) => Style | Style[]) | null => {
  if (layerInfo.sourceDescription.vectorRenderer && layerInfo.sourceDescription.vectorRenderer!.def) {
    return (feature: Feature, resulution: number) => {
      return rendererPartToStyle(layerInfo.sourceDescription.vectorRenderer!.def!, feature)
    }
  }

  return (feature: Feature, resulution: number) => {
    const geometryType = feature.getGeometry().getType()
    if (geometryType == GeometryType.Point || geometryType == GeometryType.MultiPoint) {
      return mapService.drawing.getPointStyle(DrawSource.Default)
    } else if (geometryType == GeometryType.LineString || geometryType == GeometryType.MultiLineString) {
      return mapService.drawing.getLineStringStyle(DrawSource.Default)
    } else {
      return mapService.drawing.getPolygonStyle(DrawSource.Default)
    }
  }
}
